/**
 * Adressage des écrans et des fiches — module PUR.
 *
 * ── Ce qu'il répare ─────────────────────────────────────────────────────────
 *
 * L'application n'avait AUCUNE adresse par écran : `localhost:3002/` quel que
 * soit l'endroit où l'on se trouvait. Conséquences mesurées le 14/08/2026 :
 * le bouton « Précédent » du navigateur ne faisait rien, F5 renvoyait au
 * tableau de bord, aucun écran ne pouvait être mis en favori, aucune fiche ne
 * pouvait être envoyée par lien, et Ctrl+clic n'ouvrait jamais d'onglet — les
 * 51 entrées de la barre latérale étaient des `<button>`, pas des liens.
 *
 * ── Le choix qui gouverne le module : la RÉFÉRENCE, jamais l'identifiant ────
 *
 * Une fiche est désignée dans l'URL par sa référence lisible — `BIO24-NC-2026-014`,
 * `EQ03`, `PR-METRO-001` — et non par son `documentId` Strapi
 * (`p238o1n4jnwed99dt4wtecm6`). Trois raisons, dans l'ordre d'importance :
 *
 *  1. c'est la référence qu'on lit dans un courriel, qu'on cite en réunion et
 *     qu'on retrouve sur un compte rendu : un lien doit dire OÙ il mène ;
 *  2. le `documentId` change si la fiche est recréée par une reprise de
 *     données — un lien collé dans un courriel de 2026 doit encore ouvrir la
 *     bonne fiche en 2028 ;
 *  3. un identifiant opaque dans une URL partagée est une fuite gratuite de la
 *     structure interne.
 *
 * La conversion référence → `documentId` se fait à l'ouverture, contre les
 * données déjà chargées. Elle peut ÉCHOUER (fiche inexistante, ou droits
 * insuffisants) et ce cas est traité comme un cas nominal, pas comme un bug :
 * cf. `Resolution` plus bas.
 *
 * ── Slugs français et STABLES ───────────────────────────────────────────────
 *
 * ⚠️ Un slug publié est un contrat : il vit dans des courriels, des favoris,
 * des comptes rendus de revue. Le renommer casse des liens que personne ne
 * pourra réparer. On en AJOUTE, on n'en renomme pas.
 */
import type { GedView, CleSelection } from '@/lib/types';

/** Où le paramètre d'une fiche se range dans le store, pour chaque écran. */
export interface Adressable {
  /** Segment d'URL, en français, stable à jamais. */
  slug: string;
  /** Clé de sélection du store, si l'écran ouvre une fiche. */
  cle?: CleSelection;
  /** La clé de sélection porte DÉJÀ la référence lisible : il n'y a rien à
   *  convertir, et donc aucun registre à attendre. C'est le cas d'un document
   *  (`selectedCode` est le code) et d'un poste (`selectedPoste` est le code du
   *  poste). Sans ce drapeau, ces vues resteraient bloquées en « lien en
   *  attente » jusqu'à l'arrivée d'un registre qu'elles n'utilisent pas. */
  directe?: boolean;
  /** Registre où retrouver la fiche par sa référence. */
  registre?: 'nc' | 'action' | 'recl' | 'audit' | 'rdd' | 'equip' | 'doc';
  /** Nom de l'objet, pour les messages d'erreur adressés à un humain. */
  objet?: string;
}

/**
 * La table d'adressage.
 *
 * Toute vue absente de cette table reste atteignable dans l'application mais
 * n'a pas d'adresse propre : elle retombe sur `/`. C'est un choix assumé pour
 * les écrans qui n'ont de sens qu'en contexte (saisie CQI d'une série en
 * cours, revue d'un document en circuit), pas un oubli — les partager par
 * lien mènerait à un écran vide.
 */
export const ADRESSES: Partial<Record<GedView, Adressable>> = {
  dash: { slug: '' },
  list: { slug: 'documents' },
  doc: { slug: 'documents', cle: 'selectedCode', directe: true, registre: 'doc', objet: 'ce document' },
  req: { slug: 'demandes' },
  diff: { slug: 'diffusion' },
  retrait: { slug: 'retrait-urgence' },
  nc: { slug: 'non-conformites', cle: 'selectedNC', registre: 'nc', objet: 'cette non-conformité' },
  actions: { slug: 'plan-actions', cle: 'selectedAction', registre: 'action', objet: 'cette action' },
  recl: { slug: 'reclamations', cle: 'selectedRecl', registre: 'recl', objet: 'cette réclamation' },
  audits: { slug: 'audits', cle: 'selectedAudit', registre: 'audit', objet: 'cet audit' },
  // L'auto-évaluation a une adresse propre : c'est un écran qu'on envoie en
  // lien à un technicien (« fais celle de ta paillasse »), et il n'a besoin
  // d'aucun contexte pour s'ouvrir.
  autoeval: { slug: 'auto-evaluation' },
  rdd: { slug: 'revue-direction', cle: 'selectedRdd', registre: 'rdd', objet: 'cette revue' },
  risques: { slug: 'risques' },
  objectifs: { slug: 'objectifs' },
  kpi: { slug: 'indicateurs' },
  situations: { slug: 'mises-en-situation' },
  revueref: { slug: 'intervalles-reference' },
  criteres: { slug: 'criteres-acceptation' },
  continuite: { slug: 'continuite' },
  suggestions: { slug: 'suggestions' },
  retours: { slug: 'retours-usagers' },
  rdv: { slug: 'demandes-rendez-vous' },
  /* Centre des notifications (06/10/2026). Adressable — c'est un registre qu'on
     ouvre pour répondre à « pourquoi n'ai-je rien reçu ? », et cette question se
     transmet par lien, souvent depuis un courriel ou une conversation. */
  notifs: { slug: 'notifications' },
  /* Demandes de compte (07/10/2026) : statuées dans le paramétrage, onglet Utilisateurs. */
  utilisateurs: { slug: 'utilisateurs' },
  equip: { slug: 'equipements', cle: 'selectedEquip', registre: 'equip', objet: 'cet équipement' },
  /* L3 (10/10/2026) : l'écran ne porte plus que les lots — « Matériel ». Le slug
     `fournisseurs` est CONSERVÉ : c'est une adresse publiée. */
  fourn: { slug: 'fournisseurs' },
  soustraitants: { slug: 'sous-traitants' },
  correspondants: { slug: 'correspondants' },
  /* L'annuaire (lot 2, 20/10/2026) : la fiche par sa référence, qui EST l'identifiant métier. */
  annuaire: { slug: 'annuaire', cle: 'selectedAnnuaire', directe: true, objet: 'cette fiche' },
  revuetiers: { slug: 'revue-contrats' },
  /* MoMent — référentiel du dépistage T21 (lot L1, 11/10/2026). */
  moment: { slug: 'moment' },
  momentdossiers: { slug: 'moment-dossiers' },
  produits: { slug: 'produits' },
  stock: { slug: 'stock' },
  cqtdb: { slug: 'qualite-analytique' },
  cqi: { slug: 'cqi', cle: 'selectedCqiCarte', directe: true, objet: 'cette carte' },
  /* ⚠️ ABSENTE JUSQU'AU 27/08/2026 (relevé BLOQ-02) : l'entrée de menu « CQI —
     Saisie de la série » pointait sur `/`. Une vue sans adresse retombe sur la
     racine (`if (!a) return '/'`), ce qui est le bon repli — mais donne un lien
     de menu qui RENVOIE AU TABLEAU DE BORD sans erreur ni page manquante. Le
     défaut ne se voit qu'en cliquant. Le contrôle statique
     `backend/tests/qualite-adressage-vues.test.js` refuse désormais qu'une vue
     de la navigation n'ait pas d'adresse. */
  cqisaisie: { slug: 'cqi-saisie' },
  eeq: { slug: 'eeq' },
  eil: { slug: 'eil' },
  cqeenvois: { slug: 'cqe-envois' },
  cal: { slug: 'calibrations' },
  mat: { slug: 'materiaux' },
  analytes: { slug: 'analytes' },
  metro: { slug: 'metrologie' },
  maintenances: { slug: 'maintenances' },
  relevetemp: { slug: 'releves-temperature' },
  refperf: { slug: 'criteres-performance' },
  dvm: { slug: 'verification-methodes' },
  incertitude: { slug: 'incertitude' },
  org: { slug: 'organigramme' },
  hab: { slug: 'habilitations' },
  proc: { slug: 'processus' },
  auditview: { slug: 'vue-auditeur' },
  iso: { slug: 'correspondance-iso' },
  /* Étape 5 du modèle de données : la page se PARTAGE — « regarde le constat
     sur les initiales » — et s'imprime pour le dossier d'audit. */
  coherence: { slug: 'coherence' },
  planning: { slug: 'planning' },
  /* Les règles d'affectation (lot 2, 13/09/2026). L'écran s'ENVOIE — « voilà
     les règles que le prévisionnel applique » — d'où une adresse propre. */
  reglesplanning: { slug: 'regles-planning' },
  /* Le calendrier des conventions avec les cliniques : on l'ENVOIE (« voilà les passages du mois »). */
  calendrier: { slug: 'calendrier' },
  postes: { slug: 'postes' },
  // La fiche de poste s'ENVOIE : « voilà ce qu'on attend de toi en garde ».
  // Elle est donc adressée par le CODE du poste, pas par un identifiant.
  fichePoste: { slug: 'fiche-de-poste', cle: 'selectedPoste', directe: true, objet: 'ce poste' },
  /* Les check-lists de poste (RQ, 05/10/2026). Une adresse PROPRE et non un
     onglet de « postes » : on envoie le lien (« la liste de garde est à
     corriger ici »), et elles se paramètrent indépendamment des postes. */
  checklists: { slug: 'check-lists-de-poste' },
  absences: { slug: 'absences' },
  mesconges: { slug: 'mes-conges' },
  presences: { slug: 'presences' },
  paie: { slug: 'variables-paie' },
  /* Les habilitations s'ouvrent SUR UNE PERSONNE quand on vient de la matrice
     de compétences : « qui est habilité à quoi » se lit en tableau, mais se
     MODIFIE dans un dossier. Sans cette adresse, le clic déposait sur l'écran
     et laissait retrouver la ligne à la main (25/08/2026). */
  rhhab: { slug: 'habilitations-rh', cle: 'selectedPersonne', directe: true, objet: 'cette personne' },
  mondossier: { slug: 'mon-dossier' },
  /* Le dossier RH s'ouvre SUR UNE PERSONNE (06/10/2026) : depuis le 06/10,
     l'organigramme ne donne plus qu'un résumé en lecture, et c'est son bouton
     « Ouvrir le dossier RH » qui amène ici. Sans cette adresse, le bouton
     déposait sur la liste et laissait retrouver la ligne à la main — le même
     défaut que la matrice de compétences avait le 25/08.
     ⚠️ MÊME CLÉ que `rhhab` et `org` (`selectedPersonne`) : les trois écrans
     s'ouvrent sur la même notion — une personne par son `orgId` — et chacun la
     consomme puis la remet à `null`. Une quatrième clé pour la même chose
     aurait fait divergir leurs plomberies. */
  dossiers: { slug: 'dossiers-rh', cle: 'selectedPersonne', directe: true, objet: 'ce dossier' },
  monplanning: { slug: 'mon-planning' },
  mespresences: { slug: 'mes-presences' },
  meshabilitations: { slug: 'mes-habilitations' },
  /* Le parcours d'habilitation (lot 1, 17/10/2026) : le mien depuis le profil ;
     celui de quelqu'un s'ENVOIE (« regarde où il en est ») — adressé par la
     clé de la personne, comme les habilitations. */
  monparcours: { slug: 'mon-parcours' },
  parcours: { slug: 'parcours', cle: 'selectedPersonne', directe: true, objet: 'cette personne' },
  rhjournal: { slug: 'journal-rh' },
  /* Le référentiel des analyses — UNE ADRESSE PAR EXAMEN depuis le 17/10/2026
     (Ines : « on ne sait pas où chercher ») : `/manuel-prelevement/GLY` ouvre
     la fiche de la glycémie, et c'est ce lien qu'on envoie à une collègue.
     La clé est `selectedRef`, qui porte le CODE de l'examen — directe, rien à
     convertir. Elle accepte aussi la forme historique « examen:GLY » de la
     recherche globale, et « parametre:X » / « technique:X » pour les deux
     autres listes (qui ne s'adressent pas encore : l'écran consomme la demande
     et la remet à null, l'URL redevient celle de la liste).
     ⚠️ L'entrée de menu s'appelle « Référentiel des analyses » et l'adresse
     disait `/manuel-prelevement` (relevé SYS-16). Le slug est un CONTRAT :
     changer celui d'un écran déjà partagé casse les liens envoyés. On le laisse
     donc en place, et c'est le LIBELLÉ qui devra trancher — voir la note
     d'arbitrage. Ne pas renommer sans décision. */
  manuel: { slug: 'manuel-prelevement', cle: 'selectedRef', directe: true, objet: 'cet examen' },
};

/**
 * TOUTES les clés de sélection adressables, dérivées de la table ci-dessus.
 *
 * ⚠️ ELLE SERT À REFERMER (relevé du 27/08/2026, P0.1). `appliquerAdresse`
 * posait la vue et la sélection demandée, mais n'effaçait JAMAIS les autres :
 * revenir de `/non-conformites/BIO24-NC-2026-014` à `/non-conformites` par le
 * bouton « Précédent » changeait bien l'adresse et laissait `selectedNC` en
 * place — la fiche restait ouverte à l'écran. L'utilisateur appuyait encore, et
 * sortait de l'application.
 *
 * Dérivée de `ADRESSES` et non écrite à la main : une fiche adressable ajoutée
 * demain se refermera sans que personne n'ait à y penser.
 */
export const CLES_ADRESSABLES: CleSelection[] = [...new Set(
  (Object.values(ADRESSES) as Adressable[]).map((a) => a.cle).filter(Boolean) as CleSelection[],
)];

/** Index inverse, construit une fois. Les vues « liste » et « fiche » d'un même
 *  registre partagent leur slug (`documents` / `documents/PR-METRO-001`) : la
 *  vue LISTE gagne, la fiche se déduit de la présence d'une référence. */
const PAR_SLUG = new Map<string, { vueListe: GedView; vueFiche?: GedView }>();
for (const [vue, a] of Object.entries(ADRESSES) as [GedView, Adressable][]) {
  const entree = PAR_SLUG.get(a.slug) || { vueListe: vue };
  if (a.cle) entree.vueFiche = vue;
  else entree.vueListe = vue;
  PAR_SLUG.set(a.slug, entree);
}

/** Chemin d'un écran, avec la référence de la fiche ouverte s'il y en a une. */
export function cheminDe(vue: GedView, reference?: string | null): string {
  const a = ADRESSES[vue];
  if (!a) return '/';
  const ref = String(reference || '').trim();
  if (!a.cle || !ref) return `/${a.slug}`;
  // `encodeURIComponent` et non une concaténation crue : une référence peut
  // porter un espace (« R-STOCK A », « TEL 1 » au parc d'équipements).
  return `/${a.slug}/${encodeURIComponent(ref)}`;
}

export interface Cible {
  vue: GedView;
  /** Référence LISIBLE, telle qu'écrite dans l'URL. Reste à résoudre. */
  reference: string | null;
  /** L'adresse a-t-elle été comprise ? `false` = chemin inconnu. */
  connue: boolean;
}

/** Lecture d'un chemin. Ne touche à rien : elle DIT ce que l'URL désigne. */
export function lireChemin(chemin: string): Cible {
  const propre = String(chemin || '/').split('?')[0].split('#')[0];
  const bouts = propre.split('/').filter(Boolean).map((b) => decodeURIComponent(b));
  if (bouts.length === 0) return { vue: 'dash', reference: null, connue: true };

  const entree = PAR_SLUG.get(bouts[0] as string);
  // Une adresse inconnue n'est PAS le tableau de bord : reposer silencieusement
  // sur l'accueil ferait passer un lien mort pour un lien valide, et le
  // destinataire chercherait longtemps la fiche qu'on lui a « envoyée ».
  if (!entree) return { vue: 'dash', reference: null, connue: false };

  const ref = bouts.length > 1 ? (bouts[1] as string) : null;
  if (ref && entree.vueFiche) return { vue: entree.vueFiche, reference: ref, connue: true };
  // Une référence sur un écran qui n'ouvre pas de fiche : on garde l'écran et
  // on oublie la référence, plutôt que de refuser tout le lien.
  return { vue: entree.vueListe, reference: null, connue: true };
}

/** Ce que devient une tentative d'ouverture par lien. */
export type Resolution =
  | { etat: 'ok'; valeur: string }
  /** Le registre n'est pas encore chargé — il faut réessayer, pas conclure. */
  | { etat: 'attente' }
  /** Aucune fiche ne porte cette référence, ou elle n'est pas accessible. */
  | { etat: 'introuvable'; message: string };

/**
 * Résout une référence lisible en `documentId`, contre un registre déjà chargé.
 *
 * ⚠️ Les trois états sont distincts et ne se confondent jamais. « Pas encore
 * chargé » n'est pas « introuvable » : conclure trop tôt afficherait « cette
 * non-conformité n'existe pas » sur une fiche parfaitement valide, pendant la
 * demi-seconde où le registre arrive. C'est le défaut classique de ce genre de
 * mécanisme, et il fait perdre confiance dans TOUS les liens partagés.
 */
export function resoudre(
  reference: string,
  registre: { ref?: string; code?: string; nom?: string; documentId?: string }[] | null | undefined,
  objet: string,
): Resolution {
  if (!registre) return { etat: 'attente' };
  const cherche = reference.trim().toLowerCase();
  const trouve = registre.find((r) => (
    String(r.ref || '').toLowerCase() === cherche
    || String(r.code || '').toLowerCase() === cherche
    || String(r.nom || '').toLowerCase() === cherche
  ));
  if (trouve) return { etat: 'ok', valeur: String(trouve.documentId || trouve.code || trouve.ref || '') };
  if (registre.length === 0) return { etat: 'attente' };
  return {
    etat: 'introuvable',
    // Le message dit les DEUX causes possibles sans trancher, parce qu'on ne
    // peut pas les distinguer côté écran — et laisser croire à une suppression
    // là où il s'agit d'un droit manquant enverrait chercher au mauvais endroit.
    message: `« ${reference} » ne correspond à aucune fiche que vous puissiez ouvrir. `
      + `Soit ${objet} n’existe pas (référence erronée, fiche supprimée), soit elle ne vous est pas accessible. `
      + `Demandez au responsable qualité si le lien vous a été transmis par erreur.`,
  };
}
